import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { message } from 'antd';

import { CORE_QUERY_KEYS } from 'app/hooks/data/core/admin/hotels';
import type {
  HotelDiscover,
  HotelDiscoverForm,
} from 'app/typings/hotels/Hotel/HotelDiscover';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const hotelDiscoversPath = 'hotels/:hotelId/discover' as const;

export function useGetHotelDiscover({
  hotelId,
  forceDisable,
}: {
  hotelId?: number;
  forceDisable?: boolean;
}) {
  return useQuery<HotelDiscover>({
    queryKey: [hotelDiscoversPath, { hotelId }],
    queryFn: !!hotelId
      ? () =>
          apiRequest({
            path: hotelDiscoversPath,
            params: { hotelId: hotelId?.toString() },
          })
      : skipToken,
    meta: { errorMessage: 'Error fetching discover' },
    enabled: !!hotelId && !forceDisable,
    refetchOnWindowFocus: false,
  });
}

export function useSaveHotelDiscover({ hotelId }: { hotelId: number }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: HotelDiscoverForm) =>
      apiMutation(
        'POST',
        {
          path: hotelDiscoversPath,
          params: { hotelId: hotelId.toString() },
        },
        payload
      ) as Promise<void>,
    onSuccess: () => {
      message.success('Discover successfully saved!');
      queryClient.invalidateQueries({
        queryKey: [hotelDiscoversPath, { hotelId }],
      });
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.hotelInfo],
      });
    },
    onError: () => {
      message.error('Error saving discover');
    },
  });
}
